import type { ModelRelease, TimelineRelease } from "../types/model";
import { dateGroupLabel, formatCount } from "./format";
import { type Language, localizeDataValue, ui } from "./localization";
import {
  type OrganizationIndex,
  organizationAttributionLabel,
  organizationGroupKey,
  organizationHeadingIds
} from "./organizations";
import type { GroupMode } from "./query-models";
import { entryModelCount } from "./release-groups";

export interface Block {
  key: string;
  title: string | null;
  organizationIds: string[] | null;
  records: ModelRelease[];
  /** Cards this vendor has in the whole result, not only the loaded page. */
  count?: number;
}

export interface Section {
  key: string;
  title: string | null;
  subtitle: string | null;
  countLabel: string | null;
  blocks: Block[];
}

/**
 * The numbers printed beside the list's headings, keyed like the sections and
 * vendor blocks `buildSections` makes. They are counted over every matching
 * card, so a heading shows its full count from the start instead of the part
 * of it loaded so far (owner decision 2026-10-29). Company and category
 * headings count models, a group card adding its matching members; a date
 * heading counts its releases (cards), and a vendor under a date its cards.
 */
export interface SectionCounts {
  sections: Map<string, number>;
  vendors: Map<string, number>;
}

function groupBy<T>(items: T[], keyOf: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const current = map.get(key);
    if (current) current.push(item);
    else map.set(key, [item]);
  }
  return map;
}

function add(map: Map<string, number>, key: string, count: number) {
  map.set(key, (map.get(key) ?? 0) + count);
}

export function buildSectionCounts(
  entries: readonly TimelineRelease[],
  group: GroupMode,
  language: Language
): SectionCounts {
  const sections = new Map<string, number>();
  const vendors = new Map<string, number>();
  for (const entry of entries) {
    if (group === "company") {
      add(sections, organizationGroupKey(entry), entryModelCount(entry));
    } else if (group === "category") {
      add(sections, entry.mainCategory, entryModelCount(entry));
    } else if (group === "date") {
      const key = dateGroupLabel(entry, language).key;
      add(sections, key, 1);
      add(vendors, `${key}:${organizationGroupKey(entry)}`, 1);
    }
  }
  return { sections, vendors };
}

/**
 * Lay the loaded cards out under their headings. Every count comes from
 * `counts`, built over the whole result, never from the loaded `records`.
 */
export function buildSections(
  records: ModelRelease[],
  group: GroupMode,
  language: Language,
  organizations: OrganizationIndex,
  counts: SectionCounts
): Section[] {
  if (group === "none") {
    return [
      {
        key: "all",
        title: null,
        subtitle: null,
        countLabel: null,
        blocks: [{ key: "all", title: null, organizationIds: null, records }]
      }
    ];
  }

  if (group === "company") {
    return [...groupBy(records, organizationGroupKey).entries()].map(
      ([groupKey, models]) => {
        const organizationIds = organizationHeadingIds(models[0]!);
        return {
          key: groupKey,
          title: organizationAttributionLabel(
            organizationIds,
            organizations,
            language
          ),
          subtitle: `${ui(language, "latest")} ${models[0] ? dateGroupLabel(models[0], language).title : ""}`,
          countLabel: formatCount(counts.sections.get(groupKey) ?? 0, "model", language),
          blocks: [
            {
              key: groupKey,
              title: null,
              organizationIds: null,
              records: models
            }
          ]
        };
      }
    );
  }

  if (group === "category") {
    return [...groupBy(records, (record) => record.mainCategory).entries()].map(
      ([category, models]) => ({
        key: category,
        title: localizeDataValue(category, "category", language),
        // The count takes the right-hand slot every other grouping uses.
        subtitle: null,
        countLabel: formatCount(counts.sections.get(category) ?? 0, "model", language),
        blocks: [
          { key: category, title: null, organizationIds: null, records: models }
        ]
      })
    );
  }

  const dateGroups = groupBy(
    records,
    (record) => dateGroupLabel(record, language).key
  );
  return [...dateGroups.entries()].map(([key, models]) => {
    const label = dateGroupLabel(models[0]!, language);
    const companyGroups = groupBy(models, organizationGroupKey);
    return {
      key,
      title: label.title,
      subtitle: label.subtitle,
      countLabel: formatCount(counts.sections.get(key) ?? 0, "release", language),
      blocks: [...companyGroups.entries()].map(([groupKey, companyModels]) => {
        const organizationIds = organizationHeadingIds(companyModels[0]!);
        return {
          key: `${key}:${groupKey}`,
          title: organizationAttributionLabel(
            organizationIds,
            organizations,
            language
          ),
          organizationIds,
          records: companyModels,
          count: counts.vendors.get(`${key}:${groupKey}`)
        };
      })
    };
  });
}
